/**
 * Value resolution for Rule Elements (Foundry: RuleElement#resolveInjectedProperties and
 * #resolveValue): `{item|path}` placeholders, `@actor.*` roll data, bracketed values and
 * formulas. Failures mark the Rule Element ignored and are recorded on it.
 */
import { getProperty } from "./actor-data";
import { FormulaError, evaluateFormula, replaceFormulaData } from "./formula";
import type { RuleInstance } from "./rule-elements/base";
import type { EngineState } from "./state";

export type RuleValue = string | number | boolean | object | null;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Coerce a numeric-looking string, as Foundry's ResolvableValueField does when cleaning data. */
export function cleanRuleValue(value: unknown, nullable = false): RuleValue | undefined {
  if (value === undefined) return undefined;
  if (value === null) return nullable ? null : undefined;
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (nullable && trimmed === "") return null;
    return /^-?\d+(?:\.\d+)?$/.test(trimmed) ? Number(trimmed) : trimmed || 0;
  }
  if (isPlainObject(value) && "brackets" in value) return normaliseBrackets(value);
  if (typeof value === "object") return value;
  return String(value);
}

interface Bracket { start?: number; end?: number; value: RuleValue }

function normaliseBrackets(value: Record<string, unknown>): Record<string, unknown> {
  const raw = value.brackets;
  const list = (Array.isArray(raw) ? raw : isPlainObject(raw) ? Object.values(raw) : []).filter(isPlainObject);
  const brackets: Bracket[] = list.map((b) => {
    const bracket: Bracket = { value: typeof b.value === "string" ? (cleanRuleValue(b.value) ?? 0) : ((b.value as RuleValue) ?? 0) };
    if (typeof b.start === "number") bracket.start = b.start;
    if (typeof b.end === "number") bracket.end = b.end;
    return bracket;
  });
  return { field: typeof value.field === "string" && value.field ? value.field : "actor|level", brackets };
}

/** Convert a bracketed value to the `match(when(...))` formula Foundry's migration 945 produces. */
export function bracketsToFormula(value: Record<string, unknown>): string {
  const normalised = normaliseBrackets(value);
  const fieldSpec = normalised.field as string;
  const field = fieldSpec === "item|system.level.value" ? "@item.level" : `@${fieldSpec.replace("|", ".")}`;
  const whens = (normalised.brackets as Bracket[]).map((bracket) => {
    const { start, end } = bracket;
    const v = typeof bracket.value === "number" || (typeof bracket.value === "string" && bracket.value.includes("@")) ? String(bracket.value) : JSON.stringify(bracket.value);
    if (typeof start === "number" && typeof end === "number") return `when(btwn(${field}, ${start}, ${end}), ${v})`;
    if (typeof start === "number") return `when(gte(${field}, ${start}), ${v})`;
    if (typeof end === "number") return `when(lte(${field}, ${end}), ${v})`;
    return v;
  });
  return `match(${whens.join(", ")})`;
}

export interface InjectOptions {
  injectables?: Record<string, unknown>;
  /** Record an unresolved placeholder as a problem (default true). */
  warn?: boolean;
}

/**
 * Replace `{actor|x.y}`, `{item|x.y}` and `{rule|x.y}` placeholders in strings, walking arrays
 * and objects in place. An unresolvable placeholder ignores the Rule Element.
 */
export function resolveInjectedProperties<T>(rule: RuleInstance, state: EngineState, source: T, options: InjectOptions = {}): T {
  const { injectables = {}, warn = true } = options;
  if (source === null || typeof source === "number" || (typeof source === "string" && !source.includes("{"))) return source;
  if (Array.isArray(source)) {
    for (let i = 0; i < source.length; i++) source[i] = resolveInjectedProperties(rule, state, source[i], options);
    return source;
  }
  if (isPlainObject(source)) {
    const record = source as Record<string, unknown>;
    for (const [key, value] of Object.entries(record)) {
      if (typeof value === "string" || (typeof value === "object" && value !== null)) record[key] = resolveInjectedProperties(rule, state, value, options);
    }
    return source;
  }
  if (typeof source === "string") {
    const keys = ["actor", "item", "rule", ...Object.keys(injectables).filter((k) => /^[a-z][a-z]+$/.test(k))];
    const pattern = new RegExp(String.raw`{(${keys.join("|")})\|(.*?)}`, "g");
    const all: Record<string, unknown> = { actor: state.actorView, item: rule.item, rule, ...injectables };
    return source.replace(pattern, (_match, key: string, prop: string) => {
      const value = getProperty(all[key], prop);
      if (value === undefined) {
        rule.ignored = true;
        if (warn) rule.fail(`Failed to resolve injected property "${prop}" in "${source}"`);
      }
      return String(value);
    }) as T;
  }
  return source;
}

export interface ResolveOptions {
  evaluate?: boolean;
  resolvables?: Record<string, unknown>;
  warn?: boolean;
}

/** Foundry's RuleElement#resolveValue. */
export function resolveValue(rule: RuleInstance, state: EngineState, value: unknown, defaultValue: RuleValue = 0, options: ResolveOptions = {}): RuleValue {
  const { evaluate = true, resolvables = {}, warn = true } = options;
  value ??= defaultValue ?? null;
  if (typeof value === "number" || typeof value === "boolean" || value === null) return value;
  value = resolveInjectedProperties(rule, state, structuredClone(value), { warn });
  if (Array.isArray(value)) return value;
  if (isPlainObject(value)) {
    if ("brackets" in value) return resolveValue(rule, state, bracketsToFormula(value), defaultValue, options);
    const merged = isPlainObject(defaultValue) ? { ...defaultValue, ...value } : value;
    return replaceStrings(merged, (s) => {
      const resolved = resolveValue(rule, state, s, s, options);
      return typeof resolved === "number" ? resolved : String(resolved);
    });
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!evaluate || !(trimmed.includes("@") || /^-?\d+$/.test(trimmed))) return trimmed;
    const data: Record<string, unknown> = { actor: state.actorView, item: rule.item, ...resolvables };
    const { formula, unresolved } = replaceFormulaData(trimmed, (path) => getProperty(data, path));
    if (unresolved.length > 0) {
      // Foundry lets "@target" and "@actor.conditions" fail silently; anything else is a validation failure.
      const shouldWarn = warn && !unresolved.every((u) => u.startsWith("@target.") || u.startsWith("@actor.conditions."));
      rule.ignored = true;
      if (shouldWarn) rule.fail(`unable to resolve formula, "${trimmed}"`);
      return Number(defaultValue);
    }
    try {
      return evaluateFormula(formula);
    } catch (error) {
      rule.fail(`unable to evaluate formula, "${trimmed}"${error instanceof FormulaError ? `: ${error.message}` : ""}`);
      return 0;
    }
  }
  return defaultValue;
}

function replaceStrings(value: Record<string, unknown>, replace: (s: string) => string | number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = typeof v === "string" ? replace(v) : isPlainObject(v) ? replaceStrings(v, replace) : Array.isArray(v) ? v.map((e) => (typeof e === "string" ? replace(e) : e)) : v;
  }
  return out;
}
